// The preferences and the game save every shell keeps (docs/design/shared-shell.md §4.1
// `edge/prefs.ts`, §5 A3): gin's storage.ts helpers moved here as they were, with the four things
// the two games' copies disagreed on injected: the key, the game whose room codes the save's `code`
// must be well formed for, the engine's `State` decoder and the host save's own fields. Each game's
// storage.ts still names its keys (docs/ARCHITECTURE.md "Module boundaries": only that module
// does) and builds its readers and writers from these, so the strings a page wrote before this
// file existed are the strings it writes after it: the decoders are the same decoders, and the
// host literal keeps `saveLiteral`'s order `role, code, myName, <extra>, game, oppName, handoff?`
// (test/parity/gin.storage.test.ts round-trips the legacy captures byte for byte; both games'
// storage.test.ts pin the literals). `shellStore` (§5 C2) groups the readers and writers the shell
// reducer needs over a game's keys, the object its `ShellConfig.prefs` carries. An edge because it
// takes a `Store` (./storage.ts); nothing here touches the browser itself, so prefs.test.ts runs it
// over a Map.
import {
  arrayOf,
  formatError,
  literal,
  map,
  nullable,
  number,
  object,
  optional,
  refine,
  string,
  taggedUnion,
  type Decoder,
} from '../lib/json.ts';
import { err, ok, type Result } from '../lib/result.ts';
import { packsFor, type CardPackFor } from '../lib/cards/packs.ts';
import type { DeckKind } from '../lib/cards/decks.ts';
import { appendCapped, decodeRecentGames, type RecentGame } from '../lib/recentGames.ts';
import { ROOM_CODE, isWellFormedCode, type Game } from '../lib/roomCode.ts';
import { LANGUAGE_PACKS, type LanguagePackName } from '../lib/lang/packs.ts';
import { SOUND_FONTS, type SoundFontName } from '../lib/sound/fonts.ts';
import {
  HOME_TABS,
  PLAY_MODES,
  type HomeTab,
  type PlayMode,
  type SeatCountOpts,
  type SeatCounts,
} from '../lib/shellDefaults.ts';
import type { StorageError, Store } from './storage.ts';

/** The stored tab and mode and their defaults (web/shared/lib/shellDefaults.ts): what every game's storage.ts decodes its `homeTab` and `playMode` keys against. */
export {
  DEFAULT_HOME_TAB,
  DEFAULT_PLAY_MODE,
  HOME_TABS,
  PLAY_MODES,
  type HomeTab,
  type PlayMode,
} from '../lib/shellDefaults.ts';
export const SOUND_STATES = ['on', 'off'] as const;
export type SoundState = (typeof SOUND_STATES)[number];
/**
 * `flipTable`: the pass-and-play table turned for the seat across it (web/shared/ui/shell.ts
 * `flipForFar`; docs/design/backgammon-landscape.md §6 item 7), `on` or `off` as a bare string;
 * a missing or unreadable key reads as off.
 */
export const FLIP_STATES = ['on', 'off'] as const;
export type FlipState = (typeof FLIP_STATES)[number];
/** `rememberName` sliced what it stored to this many characters (the wire cap too). */
export const NAME_MAX = 20;

/** `if (savedName)`: the legacy treated an empty string as no name. */
export const decodeName: Decoder<string> = refine(string, (s) => s !== '', 'a non-empty name');
export const decodePlayMode: Decoder<PlayMode> = literal(...PLAY_MODES);
export const decodeSoundState: Decoder<SoundState> = literal(...SOUND_STATES);
export const decodeSoundFont: Decoder<SoundFontName> = literal(...SOUND_FONTS);
export const decodeFlipState: Decoder<FlipState> = literal(...FLIP_STATES);
/**
 * The card pack a game with this deck kind may store (docs/design/card-packs.md §2): one of
 * `packsFor(kind)`, so a stored `linea` is refused under gin's key and accepted under briscola's.
 */
export const decodeCardPackFor = <K extends DeckKind>(kind: K): Decoder<CardPackFor<K>> =>
  literal(...packsFor(kind));
/** The language pack a game names its cards in (docs/design/language-packs.md §3): one of the shared packs. */
export const decodeLanguagePack: Decoder<LanguagePackName> = literal(...LANGUAGE_PACKS);

const invalid = (key: string, error: Parameters<typeof formatError>[0]): StorageError => ({
  kind: 'invalid',
  key,
  reason: formatError(error),
});

/** A bare-string key through its decoder. */
export const readTextWith = <T>(
  store: Store,
  key: string,
  decoder: Decoder<T>,
): Result<T, StorageError> => {
  const text = store.readText(key);
  if (!text.ok) return text;
  const decoded = decoder(text.value);
  return decoded.ok ? decoded : err(invalid(key, decoded.error));
};

// ---- the bare-string preferences -----------------------------------------------------------

/** A preference under one key: `read` runs its decoder, `write` stores the value as is. */
export type TextPref<T> = Readonly<{
  read: (store: Store) => Result<T, StorageError>;
  write: (store: Store, value: T) => Result<null, StorageError>;
}>;

export const textPref = <T extends string>(key: string, decoder: Decoder<T>): TextPref<T> => ({
  read: (store) => readTextWith(store, key, decoder),
  write: (store, value) => store.writeText(key, value),
});

/** `rememberName`: an empty name removes the key, anything else is stored cut to NAME_MAX. */
export const namePref = (key: string): TextPref<string> => ({
  read: (store) => readTextWith(store, key, decodeName),
  write: (store, name) =>
    name === '' ? store.remove(key) : store.writeText(key, name.slice(0, NAME_MAX)),
});

/**
 * The pass-and-play name of seat `seat` (0-based; the shell keeps seats 0 and 1 as `name` and
 * `p2Name`), under `rememberName`'s rule at `<prefix>p<seat + 1>Name`: seat 2 is `<prefix>p3Name`,
 * the key every N-seat game spelled by hand before this helper, so the names a device remembers
 * read back (prefs.test.ts reads the four games' old key names through it).
 */
export const extraNamePref = (prefix: string, seat: number): TextPref<string> =>
  namePref(`${prefix}p${String(seat + 1)}Name`);

/**
 * `ShellPrefs.seatNames` for a table of `max` seats: `extraNamePref` for the third seat up to the
 * last, index 0 the third seat, as the shell looks them up (`rememberSeatName`); [] at two.
 */
export const extraNamePrefs = (prefix: string, max: number): ReadonlyArray<TextPref<string>> =>
  Array.from({ length: Math.max(0, max - 2) }, (_, i) => extraNamePref(prefix, i + 2));

/** A bare string of digits naming one of `values`, read back as the number (`"3"` reads as 3). */
export const decodeDigitsOf = <T extends number>(values: ReadonlyArray<T>): Decoder<T> =>
  map(
    refine(
      string,
      (s) => values.some((v) => String(v) === s),
      `one of ${values.map(String).join(' | ')}`,
    ),
    (s) => Number(s) as T,
  );

/** A preference that is a number in the app and its digits in the store, so it is not a `textPref`. */
export const digitsPref = <T extends number>(key: string, decoder: Decoder<T>): TextPref<T> => ({
  read: (store) => readTextWith(store, key, decoder),
  write: (store, value) => store.writeText(key, String(value)),
});

/**
 * The seat count a home screen last chose (the game's `<prefix>players` key), one of the game's
 * `counts` as digits: uno, flip7 and briscola's one pref in one spelling (shell-hoist.md §3 M).
 */
export const seatCountPref = <T extends number>(
  key: string,
  counts: ReadonlyArray<T>,
): TextPref<T> => digitsPref(key, decodeDigitsOf(counts));

// ---- the seat count as the room's one term (uno, flip7; dry-review-2026-10.md §7 row 3) --------

/**
 * `shellStore`'s `hostExtra` for a game whose room's one term is its seat count: the host save
 * carries `seatCount`, one of `counts`, between `myName` and `game` (uno's and flip7's literal).
 */
export const seatCountExtra = <N extends number>(
  counts: SeatCounts<N>,
): Readonly<{
  decode: Decoder<SeatCountOpts<N>>;
  literal: (save: SeatCountOpts<N>) => SeatCountOpts<N>;
}> => ({
  // `object`'s Shape cannot be resolved over a type parameter (as `shellSave` notes), so the decoder is asserted to the shape it spells.
  decode: object({ seatCount: literal(...counts) }) as unknown as Decoder<SeatCountOpts<N>>,
  literal: (save) => ({ seatCount: save.seatCount }),
});

/** The room's terms over the store (what web/shared/ui/shell.ts `OptsPref` asks of a game): `read` fills the defaults where a key is missing or unreadable, `write` stores every term under its own key. */
export type StoredOptsPref<T> = Readonly<{
  read: (store: Store) => T;
  write: (store: Store, opts: T) => Result<null, StorageError>;
}>;

/** `ShellPrefs.opts` for a seated game: the count under `key` as digits (`seatCountPref`), the smallest table when the key is missing or unreadable. */
export const seatCountOptsPref = <N extends number>(
  key: string,
  counts: SeatCounts<N>,
): StoredOptsPref<SeatCountOpts<N>> => {
  const pref = seatCountPref(key, counts);
  return {
    read: (store) => {
      const stored = pref.read(store);
      return { seatCount: stored.ok ? stored.value : counts[0] };
    },
    write: (store, opts) => pref.write(store, opts.seatCount),
  };
};

/** A game's card-pack preference under its own key, validated for its deck kind like the sound font. */
export const cardPackPref = <K extends DeckKind>(key: string, kind: K): TextPref<CardPackFor<K>> =>
  textPref(key, decodeCardPackFor(kind));

export type LangPref = TextPref<LanguagePackName> &
  Readonly<{
    /** The stored pack, or the game's default when the key is missing or unreadable (the read never logs). */
    orDefault: (store: Store) => LanguagePackName;
  }>;

/** A game's language-pack preference under its own key, with the game's default beside the reader (docs/design/language-packs.md §3). */
export const langPref = (key: string, defaultName: LanguagePackName): LangPref => {
  const pref = textPref(key, decodeLanguagePack);
  return {
    ...pref,
    orDefault: (store) => {
      const stored = pref.read(store);
      return stored.ok ? stored.value : defaultName;
    },
  };
};

export type SoundPref = TextPref<SoundState> &
  Readonly<{
    /**
     * `safeGet(FX_KEY) !== 'off'`: on unless the key says off (missing or unreadable counts as
     * on). `fallback` is what a missing or unreadable key counts as instead: the boot passes
     * `false` on a touch device (docs/design/sound-fonts.md §12), where sound starts muted and the
     * tap on the speaker is the gesture that unlocks it; a remembered `on` or `off` always wins.
     */
    enabled: (store: Store, fallback?: boolean) => boolean;
  }>;

export const soundPref = (key: string): SoundPref => {
  const pref = textPref(key, decodeSoundState);
  return {
    ...pref,
    enabled: (store, fallback = true) => {
      const state = pref.read(store);
      return state.ok ? state.value === 'on' : fallback;
    },
  };
};

// ---- the finished games ----------------------------------------------------------------------

/**
 * The finished games a device remembers (web/shared/lib/recentGames.ts, the owner's "save up to 20
 * games of history in browser storage"), as JSON under one key: `read` is the stored list or []
 * (a missing, unreadable or foreign value reads as none: a record is a convenience, never a
 * save), `write` stores a list as is, `append` puts one finished game first and keeps the newest
 * `RECENT_GAMES_CAP`, re-reading the store so a second tab's records are kept too.
 */
export type RecentGamesPref = Readonly<{
  read: (store: Store) => ReadonlyArray<RecentGame>;
  write: (store: Store, games: ReadonlyArray<RecentGame>) => Result<null, StorageError>;
  append: (store: Store, game: RecentGame) => Result<null, StorageError>;
}>;

export const recentGamesPref = (key: string): RecentGamesPref => {
  const read = (store: Store): ReadonlyArray<RecentGame> => {
    const stored = store.readJson(key, decodeRecentGames);
    return stored.ok ? stored.value : [];
  };
  const write = (store: Store, games: ReadonlyArray<RecentGame>): Result<null, StorageError> =>
    store.writeJson(key, games);
  return {
    read,
    write,
    append: (store, game) => write(store, appendCapped(read(store), game)),
  };
};

// ---- the game save ---------------------------------------------------------------------------

/** `persist()` writes one of three shapes by role; the keys are in the legacy literals' order. */
export type LocalSave<S> = Readonly<{ role: 'local'; game: S }>;
export type HostSave<S, X extends object> = Readonly<{
  role: 'host';
  code: string;
  myName: string;
}> &
  Readonly<X> &
  Readonly<{
    /** null while the room waits for its first guest. */
    game: S | null;
    oppName: string | null;
    /**
     * Every guest seat's name in seat order, for a room of more than two seats
     * (docs/design/n-seat-sessions.md §7: a resume reseats each guest by name). Written only when
     * the shell has more than one seat, so every two-seat save keeps the legacy literal byte for byte.
     */
    seatNames?: ReadonlyArray<string | null>;
    /**
     * The game came from pass-and-play (the shell's `handoff`) and its remote seat has not
     * joined: a reload resumes the offer, and cancelling gives the game back to pass-and-play.
     * Written only when true, so every other host save keeps the legacy literal byte for byte.
     */
    handoff?: true;
    /**
     * When this device opened (or reopened) the room, ms since the epoch: written only while the
     * room waits for its first guest (`game` null; docs/design/lobby-resume.md D1), so a reload
     * within WAITING_RESUME_MS resumes the lobby by itself and an older one is offered. A save
     * with a game never carries it, so every mid-game literal is the legacy one byte for byte.
     */
    at?: number;
  }>;
export type GuestSave = Readonly<{ role: 'guest'; code: string; myName: string }>;
export type Save<S, X extends object> = LocalSave<S> | HostSave<S, X> | GuestSave;

export type ShellSave<S, X extends object> = Readonly<{
  decodeSave: Decoder<Save<S, X>>;
  readSave: (store: Store) => Result<Save<S, X>, StorageError>;
  writeSave: (store: Store, save: Save<S, X>) => Result<null, StorageError>;
  clearSave: (store: Store) => Result<null, StorageError>;
}>;

/**
 * The save's decoders, reader, writer and remover over a game's engine. `hostExtra` is the host
 * save's own fields between `myName` and `game` (gin's `target`, backgammon's `matchLength` and
 * `variant`): `decode` reads them off the stored object, `literal` picks them off a save in the
 * order they are written, so the game spells its fields once in each direction and the shared
 * literal around them never changes.
 */
export const shellSave = <S, X extends object>(
  cfg: Readonly<{
    /** The key the save lives under (the game's `STORAGE_KEYS.save`). */
    key: string;
    /** Whose room codes `code` must be well formed for (web/shared/lib/roomCode.ts). */
    game: Game;
    /** The engine's `State` decoder, in its literal key order. */
    decodeGame: Decoder<S>;
    hostExtra: Readonly<{ decode: Decoder<X>; literal: (save: HostSave<S, X>) => X }>;
  }>,
): ShellSave<S, X> => {
  // The legacy message spelled the length ("a 4-letter room code"); both games' codes are 4 letters.
  const roomCode = refine(
    string,
    (code) => isWellFormedCode(cfg.game, code),
    `a ${String(ROOM_CODE[cfg.game].length)}-letter room code`,
  );
  // `object`'s Shape cannot be resolved over a type parameter (whether `undefined extends S` is
  // deferred), so the two decoders holding the game state are asserted to the shapes they spell.
  const localSave = object({
    role: literal('local'),
    game: cfg.decodeGame,
  }) as Decoder<LocalSave<S>>;
  // The host object in three parts in the literal's order, so the first failing field is the one
  // a single `object` over all of them would have reported.
  const hostHead = object({ role: literal('host'), code: roomCode, myName: string });
  const hostTail = object({
    game: nullable(cfg.decodeGame),
    oppName: nullable(string),
    seatNames: optional(arrayOf(nullable(string))),
    handoff: optional(literal(true)),
    at: optional(number),
  }) as Decoder<
    Readonly<{
      game: S | null;
      oppName: string | null;
      seatNames?: ReadonlyArray<string | null>;
      handoff?: true;
      at?: number;
    }>
  >;
  const hostSave: Decoder<HostSave<S, X>> = (input) => {
    const head = hostHead(input);
    if (!head.ok) return head;
    const extra = cfg.hostExtra.decode(input);
    if (!extra.ok) return extra;
    const tail = hostTail(input);
    if (!tail.ok) return tail;
    return ok({ ...head.value, ...extra.value, ...tail.value });
  };
  const guestSave: Decoder<GuestSave> = object({
    role: literal('guest'),
    code: roomCode,
    myName: string,
  });

  // The three shapes by role in the legacy literals' order, so a refused role names them as before.
  const decodeSave: Decoder<Save<S, X>> = taggedUnion('role', {
    local: localSave,
    host: hostSave,
    guest: guestSave,
  });

  /** The `persist()` literal for a save, key for key, whatever order the caller's object had. */
  const saveLiteral = (save: Save<S, X>): Save<S, X> => {
    switch (save.role) {
      case 'local':
        return { role: 'local', game: save.game };
      case 'host':
        return {
          role: 'host',
          code: save.code,
          myName: save.myName,
          ...cfg.hostExtra.literal(save),
          game: save.game,
          oppName: save.oppName,
          ...(save.seatNames === undefined ? {} : { seatNames: save.seatNames }),
          ...(save.handoff === true ? { handoff: true } : {}),
          ...(save.at === undefined ? {} : { at: save.at }),
        };
      case 'guest':
        return { role: 'guest', code: save.code, myName: save.myName };
    }
  };

  return {
    decodeSave,
    readSave: (store) => store.readJson(cfg.key, decodeSave),
    writeSave: (store, save) => store.writeJson(cfg.key, saveLiteral(save)),
    clearSave: (store) => store.remove(cfg.key),
  };
};

// ---- the shell's store -----------------------------------------------------------------------

/**
 * The keys every shell keeps, derived from a game's prefix and save key: the pair
 * `tools/games.ts` REGISTRY.storage pins per game (`uno_` and `unoMP_v1`; gin's legacy `ginRummy_`
 * and `ginRummyMP_v1`). A game spreads `shellKeys(prefix, saveKey)` into its `STORAGE_KEYS` ahead
 * of its own keys, so it spells its prefix once and the keys it wrote before this helper existed
 * are the keys it writes after it (every storage.test.ts pins the literals; prefs.test.ts reads
 * the seven games' old key names through it).
 */
export type ShellKeysOf<P extends string, S extends string> = Readonly<{
  /** The game in progress: pass-and-play, or the host's room and game, or the guest's room (JSON). */
  save: S;
  /** The player's name, as typed (bare string, at most NAME_MAX characters). */
  name: `${P}name`;
  /** The pass-and-play second name, as typed, under the same rule; no legacy page stored it. */
  p2Name: `${P}p2Name`;
  /** The home tab last shown (bare string, one of the game's HOME_TABS). */
  homeTab: `${P}homeTab`;
  /** Online or pass-and-play (bare string, PLAY_MODES). */
  playMode: `${P}playMode`;
  /** `on` or `off` (bare string); anything but `off` counts as on. */
  sound: `${P}sound`;
  /** The sound font (web/shared/lib/sound/fonts.ts, bare string): the game's own, so another game on the origin keeps its choice (docs/design/sound-fonts.md §6). */
  soundFont: `${P}soundFont`;
  /** The finished games (JSON, newest first, at most RECENT_GAMES_CAP; web/shared/lib/recentGames.ts). */
  recentGames: `${P}recentGames`;
  /** The far seat's flip (`on`/`off`; shell.ts `flipForFar`). Every shell has the key, with or without a toggle. */
  flipTable: `${P}flipTable`;
}>;
/** The shell's keys as a game's `STORAGE_KEYS` carries them (its own keys sit beside these). */
export type ShellKeys = Readonly<Record<keyof ShellKeysOf<string, string>, string>>;

export const shellKeys = <P extends string, S extends string>(
  prefix: P,
  saveKey: S,
): ShellKeysOf<P, S> => ({
  save: saveKey,
  name: `${prefix}name`,
  p2Name: `${prefix}p2Name`,
  homeTab: `${prefix}homeTab`,
  playMode: `${prefix}playMode`,
  sound: `${prefix}sound`,
  soundFont: `${prefix}soundFont`,
  recentGames: `${prefix}recentGames`,
  flipTable: `${prefix}flipTable`,
});

/** The shell's readers and writers over one game's keys: what `web/shared/ui/shell.ts` reads `initHome` from and `shellEffects.ts` writes the effects through. */
export type ShellStore<S, X extends object, Tab extends string> = Readonly<{
  /** The keys the store was built from: what the boot names in a refusal (`badSoundFontMsg`) without the game spelling the key twice. */
  keys: ShellKeys;
  name: TextPref<string>;
  /** The pass-and-play second name, under `rememberName`'s rule; the legacy never stored it. */
  p2Name: TextPref<string>;
  homeTab: TextPref<Tab>;
  playMode: TextPref<PlayMode>;
  sound: SoundPref;
  soundFont: TextPref<SoundFontName>;
  /** The finished games, newest first, at most RECENT_GAMES_CAP (the `recordGame` effect appends). */
  recentGames: RecentGamesPref;
  /** The far seat's flip, `on` or `off` (the shell reads it as a boolean at `home/init`; the `writeFlip` effect writes it). */
  flipTable: TextPref<FlipState>;
  save: ShellSave<S, X>;
}>;

/**
 * The shell's store for a game: the eight preferences, the finished games and the save, each over
 * the game's own key, so a game's storage.ts spells its keys once and destructures its
 * `readName`/`writeName`/… from here (docs/design/shared-shell.md §5 C2 "shellStore/shellKeys onto
 * prefs.ts").
 */
export const shellStore = <S, X extends object, Tab extends string>(
  keys: ShellKeys,
  cfg: Readonly<{
    game: Game;
    decodeGame: Decoder<S>;
    hostExtra: Readonly<{ decode: Decoder<X>; literal: (save: HostSave<S, X>) => X }>;
    decodeHomeTab: Decoder<Tab>;
  }>,
): ShellStore<S, X, Tab> => ({
  keys,
  name: namePref(keys.name),
  p2Name: namePref(keys.p2Name),
  homeTab: textPref(keys.homeTab, cfg.decodeHomeTab),
  playMode: textPref(keys.playMode, decodePlayMode),
  sound: soundPref(keys.sound),
  soundFont: textPref(keys.soundFont, decodeSoundFont),
  recentGames: recentGamesPref(keys.recentGames),
  flipTable: textPref(keys.flipTable, decodeFlipState),
  save: shellSave({
    key: keys.save,
    game: cfg.game,
    decodeGame: cfg.decodeGame,
    hostExtra: cfg.hostExtra,
  }),
});

// ---- the seated games' store -----------------------------------------------------------------

/** A seated game's keys: the shell's and the seat count its two steppers share (`<prefix>players`, bare digits). */
export type SeatedKeysOf<P extends string, S extends string> = ShellKeysOf<P, S> &
  Readonly<{ players: `${P}players` }>;

/** `shellStore`'s record for a seated game, with the two preferences its `ShellConfig.prefs` carries beyond the shell's eight. */
export type SeatedStore<S, N extends number, P extends string, K extends string> = Omit<
  ShellStore<S, SeatCountOpts<N>, HomeTab>,
  'keys'
> &
  Readonly<{
    keys: SeatedKeysOf<P, K>;
    /** The seat count the home screen last chose (the shell's `prefs.opts`), under `players`. */
    opts: StoredOptsPref<SeatCountOpts<N>>;
    /** The pass-and-play names past the shell's two (the shell's `prefs.seatNames`), under `p3Name` on. */
    seatNames: ReadonlyArray<TextPref<string>>;
  }>;

/**
 * The store of a game that seats a count and nothing else (uno, flip7): `shellStore` over the
 * shell's keys and the three tabs, the host save's one field (`seatCountExtra`), the count under
 * `<prefix>players` (`seatCountOptsPref`) and one name key per seat past the shell's two up to the
 * largest table (`extraNamePrefs`). Its `keys` are the game's `STORAGE_KEYS`, so a storage.ts
 * spells its prefix and save key once and nothing else.
 */
export const seatedStore = <S, N extends number, P extends string, K extends string>(
  prefix: P,
  saveKey: K,
  cfg: Readonly<{ game: Game; decodeGame: Decoder<S>; counts: SeatCounts<N> }>,
): SeatedStore<S, N, P, K> => {
  const keys: SeatedKeysOf<P, K> = { ...shellKeys(prefix, saveKey), players: `${prefix}players` };
  return {
    ...shellStore<S, SeatCountOpts<N>, HomeTab>(keys, {
      game: cfg.game,
      decodeGame: cfg.decodeGame,
      hostExtra: seatCountExtra(cfg.counts),
      decodeHomeTab: literal(...HOME_TABS),
    }),
    keys,
    opts: seatCountOptsPref(keys.players, cfg.counts),
    seatNames: extraNamePrefs(prefix, Math.max(...cfg.counts)),
  };
};
